import { useEffect, useState } from 'react'

import { useUnit } from 'effector-react'
import { useQuery, useQueryClient } from 'react-query'
import { useSearchParams } from 'react-router-dom'

import { Page } from '@/components/layout/page/page.tsx'
import { $currentCreator, loadCreatorsFx } from '@/models/creators.ts'
import type { Creator } from '@/modules/creators/types.ts'
import { HeaderButtons } from '@/modules/creators/ui/HeaderButtons.tsx'
import { CreatorsPageSkeleton } from '@/modules/creators/ui/Skeleton.tsx'
import { UpgradeRequiredDialog } from '@/modules/creators/ui/UpgradeRequiredDialog.tsx'
import { CreatorsTable } from '@/modules/creators/views/CreatorsTable'
import { PurchaseLicenseDialog } from '@/modules/licenses/components/dialogs/purchase-license-dialog.tsx'
import { SetLicenseDialog } from '@/modules/licenses/components/dialogs/set-license-dialog.tsx'
import { UpgradeLicenseDialog } from '@/modules/licenses/components/dialogs/upgrade-license-dialog.tsx'
import { ModifyLicenseContext } from '@/modules/licenses/components/modify-license-context.tsx'
import { billingService } from '@/modules/licenses/service/billing-service.ts'
import { licensesService } from '@/modules/licenses/service/licenses-service.ts'
import type { BillingPlan, License } from '@/modules/licenses/types.ts'
import { LicenseMode } from '@/modules/licenses/types.ts'
import type { PotentialUpgrade } from '@/modules/licenses/utils.ts'
import {
  getCurrentLicense,
  getPotentialUpgradesByCreator,
} from '@/modules/licenses/utils.ts'
import { handleError } from '@/utils/error'

export const CreatorsPageView = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const isFetchingCreators = useUnit(loadCreatorsFx.pending)
  const activeCreator = useUnit($currentCreator)
  const [searchParams] = useSearchParams()
  const { data: plans, refetch: refetchPlans } = useQuery(
    ['billing'],
    billingService.getPlans,
    {
      onError: (error) =>
        handleError(error, 'Failed to get plans', refetchPlans),
    },
  )
  const { data: licenses, refetch: refetchLicenses } = useQuery(
    'licenses',
    licensesService.getLicenses,
    {
      onError: (error) =>
        handleError(error, 'Failed to get licenses', refetchLicenses),
    },
  )

  const [getLicensePlan, setGetLicensePlan] = useState<BillingPlan | undefined>(
    undefined,
  )
  const [getLicenseCreator, setGetLicenseCreator] = useState<
    Creator | undefined
  >(undefined)
  const [purchaseOpen, setPurchaseOpen] = useState(false)

  const [setLicensePlan, setSetLicensePlan] = useState<BillingPlan | undefined>(
    undefined,
  )
  const [setLicenseTarget, setSetLicenseTarget] = useState<License | undefined>(
    undefined,
  )
  const [setLicenseCreator, setSetLicenseCreator] = useState<
    Creator | undefined
  >(undefined)
  const [setLicenseOpen, setSetLicenseOpen] = useState(false)

  const [upgradingCreator, setUpgradingCreator] = useState<Creator | undefined>(
    undefined,
  )
  const [clickedUpgrade, setClickedUpgrade] = useState<
    PotentialUpgrade | undefined
  >(undefined)
  const [potentialUpgrades, setPotentialUpgrades] = useState<
    PotentialUpgrade[] | undefined
  >(undefined)
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  // Always refetching creators on fetch
  const queryClient = useQueryClient()

  useEffect(
    () => {
      loadCreatorsFx()
      queryClient.refetchQueries('creator')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  if (isFetchingCreators || !plans || !licenses) {
    return <CreatorsPageSkeleton />
  }

  const openGetLicense = (creator: Creator, plan: BillingPlan) => {
    setGetLicenseCreator(creator)
    setGetLicensePlan(plan)
    setPurchaseOpen(true)
  }
  const openSetLicense = (
    creator: Creator,
    license: License,
    plan: BillingPlan,
  ) => {
    setSetLicenseCreator(creator)
    setSetLicensePlan(plan)
    setSetLicenseTarget(license)
    setSetLicenseOpen(true)
  }
  const openUpgradeLicense = (creator: Creator, plan: BillingPlan) => {
    const potentialUpgrades = getPotentialUpgradesByCreator(
      creator,
      licenses,
      plans,
    )

    if (potentialUpgrades.length > 0) {
      setPotentialUpgrades(potentialUpgrades)
      setClickedUpgrade(
        potentialUpgrades.find((upgrade) => upgrade.plan.name === plan.name) ??
          potentialUpgrades[0],
      )
      setUpgradingCreator(creator)
      setUpgradeOpen(true)
    }
  }

  const isCustomPlan = Boolean(
    activeCreator &&
      getCurrentLicense(activeCreator, licenses)?.plan.includes('custom'),
  )

  return (
    <Page
      title="Creators"
      description="Manage all your creators here"
      headerOptions={<HeaderButtons setFilter={setSearchQuery} />}
    >
      <CreatorsTable
        searchQuery={searchQuery}
        plans={plans}
        licenses={licenses}
        openPurchaseLicense={openGetLicense}
        openSetLicense={openSetLicense}
        openUpgradeLicense={openUpgradeLicense}
      />

      <ModifyLicenseContext.Provider value={{ licenses, plans }}>
        {activeCreator &&
          searchParams.has('upgradeFeature') &&
          searchParams.has('upgradeFeatureName') && (
            <UpgradeRequiredDialog
              isUpgradeAvailable={!isCustomPlan}
              openUpgradeLicense={openUpgradeLicense}
              creator={activeCreator}
              requiredFeature={searchParams.get('upgradeFeature')!}
              plans={plans}
              featureName={
                searchParams.get('upgradeFeatureName') ?? 'this feature'
              }
            />
          )}

        {getLicensePlan && (
          <PurchaseLicenseDialog
            open={purchaseOpen}
            setOpen={setPurchaseOpen}
            clickedPlan={getLicensePlan}
            defaultToggle={LicenseMode.MONTHLY}
            creator={getLicenseCreator}
          />
        )}

        {clickedUpgrade && upgradingCreator && potentialUpgrades && (
          <UpgradeLicenseDialog
            key={'upgrade-' + upgradingCreator.id}
            open={upgradeOpen}
            setOpen={setUpgradeOpen}
            clickedUpgrade={clickedUpgrade}
            creator={upgradingCreator}
            potentialUpgrades={potentialUpgrades}
          />
        )}

        <SetLicenseDialog
          open={setLicenseOpen}
          setOpen={setSetLicenseOpen}
          plan={setLicensePlan}
          license={setLicenseTarget}
          creator={setLicenseCreator}
        />
      </ModifyLicenseContext.Provider>
    </Page>
  )
}
